import './styles/TermsAndConditionsBase.scss';

import { CSSProperties, ReactNode, Ref, useMemo } from 'react';

import {
  decodeConfigFromUrl,
  getBaseUrlWithoutQuery,
} from '../../../../helpers/common';
import { useClassHelper } from '../../../../hooks';
import { TextEditorContent } from '../../../../types';
import { Dropdown } from '../../../Dropdown';
import { Stack } from '../../../Stack';
import { TextEditor } from '../../../TextEditor';
import { Paragraph } from '../../../Typography';
import { isTextChildrenEmpty } from '../../helpers';
import { WebsheetLogoPlaceHolder } from '../WebsheetLogoPlaceHolder';

type Props = {
  getStyles: CSSProperties;
  styling: {
    logoUrl?: string;
    title: {
      text: string;
      fontFamily: string;
    };
    subtitle?: {
      text: string;
      fontFamily: string;
    };
    content: {
      text: TextEditorContent[];
      valueFontParagraph: string;
    };
  };
  footer?: ReactNode;
  afterContent?: ReactNode;
  isPortalPreview?: boolean;
  /**
   * Optional ref forwarded to the root `<main>` element. Useful for callers
   * that want to attach a DOM-level observer (visibility checks, intersection
   * observers, focus management) without wrapping the component in an extra
   * div that would disrupt the flex/grid layout the host page provides.
   */
  containerRef?: Ref<HTMLElement>;
  /**
   * Called when the logo `<img>` fails to load. The handler receives the URL
   * that failed so the caller can forward it to telemetry (e.g. log a
   * DataDog error with `errorCode: IMAGE_LOAD_FAILED`).
   */
  onImageError?: (url: string) => void;
};

const componentName = 'c-TermsAndConditionsBase';

export const TermsAndConditionsBase = ({
  styling,
  footer,
  afterContent,
  isPortalPreview = false,
  getStyles,
  containerRef,
  onImageError,
}: Props) => {
  const c = useClassHelper(componentName);
  const { logoUrl, title, content, subtitle } = styling;

  const imageConfig = useMemo(() => {
    return decodeConfigFromUrl(logoUrl || '');
  }, [logoUrl]) as any;

  const imageUrlWithoutConfig = useMemo(() => {
    return getBaseUrlWithoutQuery(logoUrl || '');
  }, [logoUrl]);

  const imgStyle = useMemo(() => {
    const style: React.CSSProperties = {};

    if (imageConfig?.imageSize) {
      style.height = imageConfig.imageSize + 'px';
    } else {
      style.height = '60px';
    }

    return style;
  }, [imageConfig]);

  const isContentEmpty = useMemo(() => {
    return isTextChildrenEmpty(content.text);
  }, [content.text]);

  const renderLogo = () => {
    if (!imageUrlWithoutConfig) {
      if (isPortalPreview) {
        return <WebsheetLogoPlaceHolder />;
      }

      return null;
    }

    return (
      <img
        src={imageUrlWithoutConfig}
        alt="logo"
        style={imgStyle}
        onError={() => onImageError?.(imageUrlWithoutConfig)}
      />
    );
  };

  return (
    <main ref={containerRef} style={getStyles} className={componentName}>
      <section className={c('__content')}>
        <Stack direction="column" gap={5} justify="center" align="center">
          {renderLogo()}

          <h1 className={`${c('__title')} apply-font-fontTitle`}>
            <Dropdown.GoogleFont.Viewer fontFamily={title.fontFamily}>
              {title.text ? title.text : 'Terms & Conditions'}
            </Dropdown.GoogleFont.Viewer>
          </h1>

          {subtitle && subtitle.text?.length > 0 && (
            <Paragraph className={c('__subtitle')}>
              <Dropdown.GoogleFont.Viewer fontFamily={subtitle.fontFamily}>
                {subtitle.text}
              </Dropdown.GoogleFont.Viewer>
            </Paragraph>
          )}

          <div className={c('__screen__content')}>
            {isContentEmpty ? (
              <TextEditor
                viewOnly
                value={[
                  {
                    type: 'paragraph',
                    children: [
                      {
                        text: 'No terms and conditions available for this locale.',
                      },
                    ],
                  },
                ]}
                valueFontParagraph={content.valueFontParagraph}
              />
            ) : (
              <TextEditor
                viewOnly
                value={content.text}
                valueFontParagraph={content.valueFontParagraph}
              />
            )}

            {afterContent}
          </div>
        </Stack>
      </section>
      <footer
        className={c('__footer', {
          '__footer--preview': isPortalPreview,
        })}
      >
        {footer}
      </footer>
    </main>
  );
};
